import * as React from "react";
import { AUTH_TOKEN_KEY } from "./constants";
import { useNavigate, useLocation } from "@reach/router";
import { Routes } from "../routes";
import { Api } from "./api";
import { AdminUserResponse } from "@authory-packages/core";

function getSessionTokenFromLocalStorage() {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

function setSessionTokenToLocalStorage(token: string | null) {
  if (token === null) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } else {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
}

export interface UserContextHooks {
  session: AdminUserResponse | null;
  sessionToken: string | null;
  setSession: (user: AdminUserResponse | null) => void;
  setSessionToken: (user: string | null) => void;
}

// Initial state is overwritten below.
export const UserContext = React.createContext<UserContextHooks>({
  session: null,
  sessionToken: null,
  setSessionToken: (t) => {},
  setSession: (u) => {},
});

export const UserSessionProvider: React.FC<{}> = ({ children }) => {
  const [session, setSession] = React.useState<AdminUserResponse | null>(null);
  const [sessionToken, setSessionTokenState] = React.useState<string | null>(
    getSessionTokenFromLocalStorage()
  );

  const setSessionToken = (token: string | null) => {
    setSessionTokenToLocalStorage(token);
    setSessionTokenState(token);
  };

  return (
    <UserContext.Provider
      value={{ session, setSession, sessionToken, setSessionToken }}
    >
      {children}
    </UserContext.Provider>
  );
};

export function useUserSession() {
  const { session, sessionToken, setSession, setSessionToken } =
    React.useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    (async () => {
      if (session === null) {
        if (sessionToken === null) {
          console.log("Session token not present. Redirecting to login.");
          if (location.pathname !== Routes.Login) {
            navigate(Routes.Login);
          }
        } else {
          console.log("Session token present. Attempting login.");
          try {
            const response = await Api.me();
            setSession(await response.data);
          } catch {
            setSessionToken(null);
            navigate(Routes.Login);
          }
        }
      }
    })();
  }, []);

  return { session, sessionToken, setSession, setSessionToken };
}
